<template>
  <ReportModuleTable
    title="Relatórios recentes"
    subtitle="Histórico de relatórios de visita técnica criados no módulo."
    :columns="columns"
    :items="reports"
  >
    <template #empty>
      <ReportEmptyState
        title="Nenhum relatório encontrado"
        description="Crie um novo relatório de visita técnica para iniciar o preenchimento e emissão."
        :icon="PhFileText"
      />
    </template>

    <template #cell-title="{ item }">
      <div>
        <p class="font-semibold text-gray-800">
          {{ item.title }}
        </p>

        <p class="text-xs text-gray-500">
          {{ getReportTypeLabel(item.report_type) }}
        </p>
      </div>
    </template>

    <template #cell-client="{ item }">
      <div>
        <p class="font-medium text-gray-700">
          {{
            getClientName(item).length > 10
              ? getClientName(item).slice(0, 10).concat("...")
              : getClientName(item)
          }}
        </p>

        <p v-if="getClientDocument(item)" class="text-xs text-gray-400">
          {{ formatarCNPJ(getClientDocument(item)) }}
        </p>
      </div>
    </template>

    <template #cell-layout="{ item }">
      <div>
        <p class="font-medium text-gray-700">
          {{ getLayoutName(item) }}
        </p>

        <p v-if="item.layout_snapshot?.name" class="text-xs text-gray-400">
          Snapshot salvo
        </p>
      </div>
    </template>

    <template #cell-visit_date="{ value }">
      {{ formatDate(value) }}
    </template>

    <template #cell-created_at="{ value }">
      {{ formatDate(value) }}
    </template>

    <template #cell-status="{ item }">
      <ReportStatusBadge :status="item.status" />
    </template>

    <template #actions="{ item }">
      <div class="flex items-center justify-center gap-2">

        <button
          v-if="item.status === 'issued' && (item.pdf_url || item.pdf_path)"
          type="button"
          title="Visualizar PDF"
          class="rounded-lg p-2 text-gray-400 transition hover:bg-green-50 hover:text-green-700"
          @click="$emit('open-pdf', item)"
        >
          <PhFilePdf class="h-5 w-5" />
        </button>

        <button
          type="button"
          :title="
            item.status === 'draft'
              ? 'Montar relatório'
              : 'Visualizar relatório'
          "
          class="rounded-lg p-2 text-gray-400 transition hover:bg-green-50 hover:text-green-700"
          @click="$emit('build', item)"
        >
          <PhWrench v-if="item.status === 'draft'" class="h-5 w-5" />
          <PhEye v-else class="h-5 w-5" />
        </button>

        <button
          v-if="item.status !== 'archived'"
          type="button"
          title="Editar relatório"
          class="rounded-lg p-2 text-gray-400 transition hover:bg-yellow-50 hover:text-yellow-600"
          @click="$emit('edit', item)"
        >
          <PhNotePencil class="h-5 w-5" />
        </button>

        <button
          v-if="item.status === 'draft'"
          type="button"
          title="Finalizar relatório"
          class="rounded-lg p-2 text-gray-400 transition hover:bg-green-50 hover:text-green-700"
          @click="$emit('finish', item)"
        >
          <PhCheckCircle class="h-5 w-5" />
        </button>

        <button
          v-if="item.status === 'finished'"
          type="button"
          title="Reabrir como rascunho"
          class="rounded-lg p-2 text-gray-400 transition hover:bg-yellow-50 hover:text-yellow-700"
          @click="$emit('reopen', item)"
        >
          <PhArrowCounterClockwise class="h-5 w-5" />
        </button>

        <button
          v-if="['finished', 'issued', 'archived'].includes(item.status)"
          type="button"
          title="Criar cópia editável"
          class="rounded-lg p-2 text-gray-400 transition hover:bg-blue-50 hover:text-blue-700"
          @click="$emit('clone', item)"
        >
          <PhCopy class="h-5 w-5" />
        </button>

        <button
          v-if="item.status !== 'archived'"
          type="button"
          title="Arquivar relatório"
          class="rounded-lg p-2 text-gray-400 transition hover:bg-red-50 hover:text-red-600"
          @click="$emit('archive', item)"
        >
          <PhArchiveBox class="h-5 w-5" />
        </button>

        <button
          v-else
          type="button"
          title="Restaurar relatório"
          class="rounded-lg p-2 text-gray-400 transition hover:bg-green-50 hover:text-green-700"
          @click="$emit('restore', item)"
        >
          <PhArrowCounterClockwise class="h-5 w-5" />
        </button>
      </div>
    </template>
  </ReportModuleTable>
</template>

<script setup>
import {
  PhArchiveBox,
  PhArrowCounterClockwise,
  PhCheckCircle,
  PhEye,
  PhFileText,
  PhNotePencil,
  PhTrash,
  PhWrench,
  PhCopy,
  PhFilePdf,
} from "@phosphor-icons/vue";

import ReportModuleTable from "../shared/ReportModuleTable.vue";
import ReportEmptyState from "../shared/ReportEmptyState.vue";
import ReportStatusBadge from "./ReportStatusBadge.vue";
import { formatarCNPJ } from "@/utils/reports/cnpj-mask";

defineProps({
  reports: {
    type: Array,
    default: () => [],
  },
});

defineEmits([
  "build",
  "edit",
  "finish",
  "reopen",
  "archive",
  "restore",
  "open-pdf",
  "clone",
]);

const columns = [
  {
    key: "title",
    label: "Relatório",
  },
  {
    key: "client",
    label: "Empresa",
  },
  {
    key: "layout",
    label: "Layout",
  },
  {
    key: "technician_name",
    label: "Técnico",
  },
  {
    key: "visit_date",
    label: "Data da visita",
  },
  {
    key: "status",
    label: "Status",
    align: "center",
  },
  {
    key: "created_at",
    label: "Criado em",
  },
];

function getClientName(report) {
  return (
    report?.client_snapshot?.trade_name ||
    report?.client_snapshot?.corporate_name ||
    report?.client?.trade_name ||
    report?.client?.corporate_name ||
    "-"
  );
}

// todo
function getClientDocument(report) {
  return report?.client_snapshot?.cnpj || report?.client?.cnpj || "";
}

function formatDate(value) {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "-";

  return new Intl.DateTimeFormat("pt-BR").format(date);
}

function getReportTypeLabel(type) {
  const labels = {
    photographic: "Relatório fotográfico",
    descriptive: "Relatório descritivo",
    free_text: "Texto livre",
    photo_text: "Foto + texto",
    checklist: "Checklist",
    action_plan: "Plano de ação",
    audit: "Auditoria",
  };

  return labels[type] || "Relatório técnico";
}

function getLayoutName(report) {
  return report?.layout_snapshot?.name || report?.layout?.name || "Sem layout";
}
</script>
